import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

// Copyright 1999-2020. Plesk International GmbH. All rights reserved.
import React, { cloneElement, Children, isValidElement, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CLS_PREFIX } from '../../constants';
import { safeInvoke, createFocusManager } from '../utils';

/**
 * `Subnav` component is used when you need a second level navigation under [Tabs](#!/Tabs) on the screen.
 * @since 0.0.42
 */
var Subnav = function Subnav(_ref) {
  var baseClassName = _ref.baseClassName,
      className = _ref.className,
      children = _ref.children,
      onKeyDown = _ref.onKeyDown,
      props = _objectWithoutProperties(_ref, ["baseClassName", "className", "children", "onKeyDown"]);

  var rootRef = useRef();

  var _useMemo = useMemo(function () {
    return createFocusManager(rootRef);
  }, []),
      focusNext = _useMemo.focusNext,
      focusPrev = _useMemo.focusPrev,
      focusFirst = _useMemo.focusFirst,
      focusLast = _useMemo.focusLast;

  var handleKeyDown = function handleKeyDown(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        focusPrev();
        break;

      case 'ArrowRight':
        e.preventDefault();
        focusNext();
        break;

      case 'Home':
      case 'PageUp':
        e.preventDefault();
        focusFirst();
        break;

      case 'End':
      case 'PageDown':
        e.preventDefault();
        focusLast();
        break;

      case 'Enter':
        e.preventDefault();
        e.target.click();
        break;
    }

    safeInvoke(onKeyDown, e);
  };

  var itemIndex = -1;
  Children.toArray(children).every(function (child, index) {
    if (!isValidElement(child)) {
      return true;
    }

    if (child.props.active) {
      itemIndex = index;
      return false;
    } else if (itemIndex === -1) {
      itemIndex = index;
    }

    return true;
  });
  var items = Children.map(children, function (child, index) {
    if (itemIndex === index) {
      return cloneElement(child, {
        tabIndex: 0
      });
    }

    return child;
  });
  return React.createElement("div", _extends({
    className: classNames(baseClassName, className)
  }, props, {
    onKeyDown: handleKeyDown,
    ref: rootRef
  }), items);
};

Subnav.propTypes = {
  /**
   * Subnav items
   * @since 0.0.42
   */
  children: PropTypes.node,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string,

  /**
   * @ignore
   */
  onKeyDown: PropTypes.string
};
Subnav.defaultProps = {
  children: null,
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "subnav"),
  onKeyDown: undefined
};
/**
 * SubnavItem component.
 */

var SubnavItem = function SubnavItem(_ref2) {
  var baseClassName = _ref2.baseClassName,
      className = _ref2.className,
      active = _ref2.active,
      children = _ref2.children,
      props = _objectWithoutProperties(_ref2, ["baseClassName", "className", "active", "children"]);

  return cloneElement(children, _objectSpread({
    className: classNames(baseClassName, active && "".concat(baseClassName, "--active"), className),
    tabIndex: -1
  }, props));
};

SubnavItem.propTypes = {
  /**
   * Is item active.
   */
  active: PropTypes.bool,

  /**
   * Content of the item
   */
  children: PropTypes.element.isRequired,

  /**
   * @ignore
   */
  className: PropTypes.string,

  /**
   * @ignore
   */
  baseClassName: PropTypes.string
};
SubnavItem.defaultProps = {
  className: null,
  baseClassName: "".concat(CLS_PREFIX, "subnav__item")
};
export default Subnav;
export { SubnavItem };